import * as React from "react";

import { createContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePushContext } from "./PushContextProvider";
import useIsVisible from "../hooks/useIsVisible";
import { debounce } from "lodash";

type contentType = {
  badgeCount: number | null;
};

const defaultContext = () => ({
  badgeCount: null,
});

interface FullContext extends contentType {
  refresh: () => void;
}
export const MemberStatusContext = createContext<FullContext>({
  ...defaultContext(),
  refresh: () => {},
});

export default ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { registerHandler, unregisterHandler } = usePushContext();
  const [context, setContext] = useState<contentType>(defaultContext());

  const refresh = () => {
    // Let's just zero out the badge count every time the app is opened until we have more
    // sophisticated read/unread states and their counts.
    setContext((prev) => ({
      ...prev,
      badgeCount: 0,
    }));
    if ("setAppBadge" in navigator) {
      // @ts-ignore
      navigator.setAppBadge(0);
    }
  };

  // Reload the state whenever the page is changed or we have re-opened the page after some period.
  // Add a debounce + delay. Delay is so that we can let users fire off quick events like markAsRead
  // and then we can consume the most up to date data (like the newest badge count).
  const debouncedRefresh = useMemo(
    () => debounce(refresh, 500, { trailing: true }), // Adjust the delay as needed (e.g., 1000ms = 1 second)
    [],
  );

  useIsVisible({
    onRefocus: async (blurSecs) => {
      if (blurSecs > 10) {
        debouncedRefresh();
      }
    },
  });
  // This might need to get tweaked, but we don't want
  // to refetch on single location change since things like search params might change frequently.
  useEffect(debouncedRefresh, [location.pathname]);

  useEffect(() => {
    const pushHandler = () => {
      // On mobile we can have multiple tabs/layers open in the app. Upon a notification, all of these may be triggered.
      // Let's not refresh if the tab is hidden.
      if (!document.hidden) {
        debouncedRefresh();
      }
    };
    registerHandler(pushHandler);

    return () => {
      unregisterHandler(pushHandler);
    };
  }, [registerHandler, unregisterHandler, debouncedRefresh]);

  return (
    <MemberStatusContext.Provider
      value={{
        ...context,
        refresh: () => debouncedRefresh(),
      }}
    >
      {children}
    </MemberStatusContext.Provider>
  );
};
